import styles from "../../styles/components/blogs/Pagination.module.css"

/**
 * Reusable Pagination Component
 * SEO-friendly pagination with anchor links for crawler discoverability
 * @param {Object} props
 * @param {number} props.currentPage - Current active page
 * @param {number} props.totalPages - Total number of pages
 * @param {function} props.onPageChange - Callback function when page changes
 * @param {Object} props.queryParams - Additional query parameters to maintain in URL
 */
export default function Pagination({ currentPage, totalPages, onPageChange, queryParams = {} }) {

  // Build URL for a given page number
  const buildPageUrl = (page) => {
    const params = new URLSearchParams()
    
    // Add query parameters (search, category, etc.)
    if (queryParams.search) params.set('search', queryParams.search)
    if (queryParams.category && queryParams.category !== 'All') params.set('category', queryParams.category)
    
    // Add page parameter (only if page > 1)
    if (page > 1) params.set('page', page.toString())
    
    const queryString = params.toString()
    return queryString ? `/blogs?${queryString}` : '/blogs'
  }

  // Handle page change with progressive enhancement
  const handlePageChange = (e, page) => {
    // Prevent default navigation if JavaScript is enabled
    e.preventDefault()
    onPageChange(page)
  }

  // Don't render if only one page
  if (totalPages <= 1) return null

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show around current page
    const range = [];
    const rangeWithDots = [];
    let l;

    // Always show first page
    range.push(1);

    // Calculate range around current page
    for (let i = currentPage - delta; i <= currentPage + delta; i++) {
      if (i < totalPages && i > 1) {
        range.push(i);
      }
    }

    // Always show last page
    range.push(totalPages);

    // Add pages and ellipsis
    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          // If gap is exactly 2, show the missing page
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          // If gap is more than 2, show ellipsis
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  const pageNumbers = getPageNumbers();
  const prevPage = currentPage > 1 ? currentPage - 1 : null
  const nextPage = currentPage < totalPages ? currentPage + 1 : null

  return (
    <div className={styles.paginationContainer}>
      <nav className={styles.pagination} aria-label="Blog pagination">
        {prevPage ? (
          <a
            href={buildPageUrl(prevPage)}
            className={styles.pageBtn}
            onClick={(e) => handlePageChange(e, prevPage)}
            aria-label="Go to previous page"
          >
            Previous
          </a>
        ) : (
          <span
            className={`${styles.pageBtn} ${styles.disabled}`}
            aria-label="Previous page (disabled)"
            aria-disabled="true"
          >
            Previous
          </span>
        )}

        {pageNumbers.map((pageNum, index) => {
          if (pageNum === '...') {
            return (
              <span key={`ellipsis-${index}`} className={styles.ellipsis} aria-hidden="true">
                ...
              </span>
            );
          }

          const isActive = currentPage === pageNum
          
          return (
            <a
              key={pageNum}
              href={buildPageUrl(pageNum)}
              className={`${styles.pageBtn} ${isActive ? styles.active : ""}`}
              onClick={(e) => handlePageChange(e, pageNum)}
              aria-label={`Go to page ${pageNum}`}
              aria-current={isActive ? "page" : undefined}
            >
              {pageNum}
            </a>
          );
        })}

        {nextPage ? (
          <a
            href={buildPageUrl(nextPage)}
            className={styles.pageBtn}
            onClick={(e) => handlePageChange(e, nextPage)}
            aria-label="Go to next page"
          >
            Next
          </a>
        ) : (
          <span
            className={`${styles.pageBtn} ${styles.disabled}`}
            aria-label="Next page (disabled)"
            aria-disabled="true"
          >
            Next
          </span>
        )}
      </nav>
    </div>
  )
}
